// 2026-06-30 - FormuLearning - 관리자 콘솔 (이슈 #30·#31·#32)
// 파일 위치: frontend/src/pages/AdminPage.tsx
// 탭: Usage(오퍼레이션 사용통계) · Logs(API/Operation 실행내역) · API Stats(일자 집계) · Catalog(APIs/Operations 정의) · Users(사용자·권한 관리)

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import ApiIntegrationList from '../features/admin/ApiIntegrationList'
import CallStats from '../features/admin/CallStats'
import CallLogList from '../features/admin/CallLogList'
import UsageStats from '../features/admin/UsageStats'
import OperationLogList from '../features/admin/OperationLogList'
import OperationCatalogList from '../features/admin/OperationCatalogList'
import UserManageList from '../features/admin/UserManageList'

type Tab = 'usage' | 'logs' | 'stats' | 'catalog' | 'users'
const TABS: { key: Tab; label: string }[] = [
  { key: 'usage', label: 'Usage' },
  { key: 'logs', label: 'Logs' },
  { key: 'stats', label: 'API Stats' },
  { key: 'catalog', label: 'Catalog' },
  { key: 'users', label: 'Users' },
]

// API/Operation 등 하위 선택용 세그먼트 토글.
function Segmented<T extends string>({ value, onChange, options }: {
  value: T; onChange: (v: T) => void; options: { key: T; label: string }[]
}) {
  return (
    <div className="mb-3 inline-flex gap-1 rounded-lg p-0.5" style={{ backgroundColor: 'var(--color-bg-panel)' }}>
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className="rounded px-3 py-1 text-xs transition-colors"
          style={value === o.key
            ? { backgroundColor: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }
            : { color: 'var(--color-text-secondary)' }}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export default function AdminPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const [tab, setTab] = useState<Tab>('usage')
  const [logMode, setLogMode] = useState<'api' | 'operation'>('operation')
  const [catalogMode, setCatalogMode] = useState<'api' | 'operation'>('api')

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg-base)' }}>
      <header
        className="flex h-14 items-center justify-between border-b px-5"
        style={{ backgroundColor: 'var(--color-bg-panel)', borderColor: 'var(--border-divider)' }}
      >
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="text-sm text-text-secondary hover:text-text-body">← Workspace</button>
          <span className="text-sm font-semibold text-text-primary">Admin · Logging &amp; Usage</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-text-secondary">{user?.name}</span>
          <button onClick={logout} className="rounded px-2 py-1 text-xs text-text-secondary hover:bg-bg-input hover:text-text-body">Sign Out</button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-6">
        <div className="mb-4 flex gap-1 border-b" style={{ borderColor: 'var(--border-divider)' }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className="px-4 py-2 text-sm transition-colors"
              style={
                tab === t.key
                  ? { color: 'var(--color-accent-teal)', borderBottom: '2px solid var(--color-accent-teal)' }
                  : { color: 'var(--color-text-secondary)' }
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'usage' && <UsageStats />}

        {tab === 'logs' && (
          <div>
            <Segmented
              value={logMode}
              onChange={setLogMode}
              options={[{ key: 'operation', label: 'Operations' }, { key: 'api', label: 'API calls' }]}
            />
            {logMode === 'operation' ? <OperationLogList /> : <CallLogList />}
          </div>
        )}

        {tab === 'stats' && <CallStats />}

        {tab === 'catalog' && (
          <div>
            <Segmented
              value={catalogMode}
              onChange={setCatalogMode}
              options={[{ key: 'api', label: 'External APIs' }, { key: 'operation', label: 'Operations' }]}
            />
            {catalogMode === 'api' ? <ApiIntegrationList /> : <OperationCatalogList />}
          </div>
        )}

        {tab === 'users' && <UserManageList />}
      </div>
    </div>
  )
}
